import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg, num, dt, today, unitsOf } from "@/lib/api";
import { PageHeader, Card, Table, StatusBadge, Field, Sel, Tag, Money, AuditTimeline } from "@/components/common";
import { Modal, SaveBtn, NewBtn } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";

const KIND = { in: ["DO masuk", "emerald"], out: ["Ditarik supplier", "amber"] };

const ConsForm = ({ kind, onClose }) => {
  const { meta, myLocs, itemById } = useAuth();
  const consItems = meta.items.filter((i) => i.consignment && i.active !== false);
  const sups = meta.suppliers.filter((s) => consItems.some((i) => i.supplier_id === s.id));
  const [f, setF] = useState({ kind, date: today(), supplier_id: sups[0]?.id, location_id: myLocs[0]?.id, do_no: "", note: "" });
  const [lines, setLines] = useState([{ item_id: "", qty: "", unit: "" }]);
  const opts = consItems.filter((i) => i.supplier_id === f.supplier_id);
  const setL = (i, p) => setLines(lines.map((l, j) => (j === i ? { ...l, ...p } : l)));
  const save = async () => {
    const body = { ...f, lines: lines.filter((l) => l.item_id && Number(l.qty) > 0).map((l) => ({ ...l, qty: Number(l.qty) })) };
    try { await api.post("/consignments", body); toast.success("Tersimpan, stok diperbarui"); onClose(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal open onOpenChange={() => onClose(false)} title={kind === "in" ? "DO konsinyasi masuk" : "Penarikan barang oleh supplier"} wide testid="cons-modal">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Supplier"><Sel testid="cons-supplier-select" value={f.supplier_id} onChange={(v) => { setF({ ...f, supplier_id: v }); setLines([{ item_id: "", qty: "", unit: "" }]); }} options={sups.map((s) => ({ value: s.id, label: s.name }))} /></Field>
        <Field label="Lokasi"><Sel testid="cons-location-select" value={f.location_id} onChange={(v) => setF({ ...f, location_id: v })} options={myLocs.map((l) => ({ value: l.id, label: l.name }))} /></Field>
        <Field label="Tanggal"><input type="date" className="inp" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="No. DO / bukti"><input className="inp" value={f.do_no} onChange={(e) => setF({ ...f, do_no: e.target.value })} data-testid="cons-do-input" /></Field>
      </div>
      {!sups.length && <div className="text-sm text-amber-700">Belum ada barang yang ditandai konsinyasi di master barang.</div>}
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2" data-testid={`cons-line-${i}`}>
          <select className="inp col-span-6" value={l.item_id} onChange={(e) => setL(i, { item_id: e.target.value, unit: itemById[e.target.value]?.base_unit })} data-testid={`cons-item-${i}`}>
            <option value="">Pilih barang</option>{opts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
          <input className="inp col-span-3 num" type="number" step="any" placeholder="Qty" value={l.qty} onChange={(e) => setL(i, { qty: e.target.value })} data-testid={`cons-qty-${i}`} />
          <select className="inp col-span-2" value={l.unit} onChange={(e) => setL(i, { unit: e.target.value })}>{unitsOf(itemById[l.item_id]).map((u) => <option key={u}>{u}</option>)}</select>
          <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="col-span-1 text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
        </div>
      ))}
      <button onClick={() => setLines([...lines, { item_id: "", qty: "", unit: "" }])} className="text-sm font-semibold text-emerald-700 text-left" data-testid="cons-add-line">+ Tambah barang</button>
      <Field label="Catatan"><input className="inp" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
      <SaveBtn onClick={save} testid="cons-save-button" />
    </Modal>
  );
};

const ConsDetail = ({ id, onClose }) => {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get(`/consignments/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const c = d.consignment;
  const verify = async () => { try { await api.post(`/consignments/${id}/verify`); toast.success("Diverifikasi"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Modal open onOpenChange={() => onClose()} title={`${c.code} · ${KIND[c.kind][0]}`} wide testid="cons-detail-modal">
      <div className="text-sm text-slate-600">{c.supplier_name} · {c.location_name} · {c.date} · DO {c.do_no || "-"} · <StatusBadge s={c.status} /></div>
      <Table rows={c.lines} cols={[{ h: "Barang", k: "item_name" }, { h: "Qty", right: true, r: (l) => <span className="num">{num(l.qty)} {l.unit}</span> },
        { h: "Qty stok", right: true, r: (l) => <span className="num">{num(l.base_qty)} {l.base_unit}</span> }, { h: "Nilai", right: true, r: (l) => <Money v={l.value} /> }]} />
      {c.status === "waiting_verification" && can("consignment.verify") && <button onClick={verify} className="h-9 px-3 rounded-md bg-emerald-600 text-white text-sm font-semibold self-end" data-testid="cons-verify-button">Verifikasi (sudah dicek)</button>}
      <details><summary className="text-sm font-semibold cursor-pointer">Jejak audit</summary><AuditTimeline items={d.audit} /></details>
    </Modal>
  );
};

export default function Consignment() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/consignments").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title="Konsinyasi" sub="Catat DO barang titipan supplier dan penarikan barang yang tidak laku. Stok langsung diperbarui.">
        {can("consignment.do") && <>
          <NewBtn onClick={() => setForm("in")} testid="new-cons-in-button">DO masuk</NewBtn>
          <NewBtn onClick={() => setForm("out")} testid="new-cons-out-button">Penarikan supplier</NewBtn>
        </>}
      </PageHeader>
      <Card>
        <Table testid="cons-table" rows={rows} onRow={(r) => setSel(r.id)} empty="Belum ada transaksi konsinyasi" cols={[
          { h: "Kode", r: (r) => <span className="num text-xs font-semibold text-emerald-700">{r.code}</span> },
          { h: "Tanggal", k: "date" },
          { h: "Jenis", r: (r) => <Tag tone={KIND[r.kind][1]}>{KIND[r.kind][0]}</Tag> },
          { h: "Supplier", k: "supplier_name" }, { h: "Lokasi", k: "location_name" },
          { h: "Barang", r: (r) => <span className="text-xs">{r.lines.map((l) => `${l.item_name} ${num(l.qty)} ${l.unit}`).join(", ")}</span> },
          { h: "Status", r: (r) => <StatusBadge s={r.status} /> },
          { h: "Oleh", r: (r) => <span className="text-xs">{r.created_by.name} · {dt(r.created_at)}</span> },
        ]} />
      </Card>
      {form && <ConsForm kind={form} onClose={(ok) => { setForm(null); ok && load(); }} />}
      {sel && <ConsDetail id={sel} onClose={() => { setSel(null); load(); }} />}
    </div>
  );
}
